import { PopinEnum } from 'pleinchamp-api-client';
import { isServer } from '@utils/server';
import { urlEnum } from '@utils/url';

export interface PopinLocalStorage {
  key: PopinEnum;
  value: number;
}

/**
 * Get all the pop-in informations store in localstorage
 *
 * @returns Array<PopinLocalStorage>
 */
export function getPopinLocalStorageInfo(): Array<PopinLocalStorage> {
  if (isServer()) {
    return [];
  }
  const data = window.localStorage.getItem('popin');

  if (data === null) {
    return [];
  }
  return JSON.parse(data);
}
/**
 * Add a popin to the local storage
 * @param popInToAdd PopinEnum
 */
export function addPopinToLocalSorage(popInToAdd: PopinEnum) {
  if (isServer()) {
    return;
  }
  const data = getPopinLocalStorageInfo();
  const index = data.findIndex(item => item.key === popInToAdd);

  if (index === -1) {
    data.push({ key: popInToAdd, value: Date.now() });
  } else {
    data[index] = { key: popInToAdd, value: Date.now() };
  }
  window.localStorage.removeItem('popin');
  window.localStorage.setItem('popin', JSON.stringify(data));
}

/**
 * Parse the local storage to retrieves the last added pop in based on its date.
 * @returns The most recent added popin
 */
export function getMostRecentPopinFromLocalStorage(): PopinLocalStorage | null {
  if (isServer()) {
    return null;
  }
  const data = getPopinLocalStorageInfo();

  if (data.length === 0) {
    return null;
  }

  return data.reduce((a, b) => (new Date(a.value) > new Date(b.value) ? a : b));
}

/**
 * Get a pop in by its key.
 * @param key
 * @return PopinLocalStorage
 */
export function getPopinByKey(key: PopinEnum): PopinLocalStorage | null {
  const data = getPopinLocalStorageInfo();
  if (data.length === 0) {
    return null;
  }
  const index = data.findIndex(item => item.key === key);

  if (index === -1) {
    return null;
  }
  return data[index];
}

/**
 * Determine whether a popin exist and its cooldown is over.
 * @param key PopinEnum
 * @param delay expect second
 */
export function isPopinCooldownOver(key: PopinEnum, delay: number) {
  const popinByKey = getPopinByKey(key);

  if (popinByKey === null) {
    return true;
  }

  return Date.now() >= popinByKey.value + delay * 1000;
}

/**
 * Is the time of the most recent popin passed to display a new one
 * The waiting time is 30min
 * @returns boolean
 */
export function isTimeToDisplayFromMostRecentPopinUp() {
  const mostRecentPopin = getMostRecentPopinFromLocalStorage();
  const interval = process.env.NEXT_PUBLIC_INTERVAL_POPIN
    ? parseInt(process.env.NEXT_PUBLIC_INTERVAL_POPIN, 10)
    : 1800000;

  return mostRecentPopin ? Date.now() - mostRecentPopin.value > interval : true;
}

/**
 * List of URLs where we don't want any popin
 */
export const disabledURLs = [
  urlEnum.login.valueOf(),
  urlEnum.signUp.valueOf(),
  urlEnum.signUpPro.valueOf(),
  urlEnum.meteoPlv.valueOf(),
  urlEnum.notifications.valueOf(),
  urlEnum.profile.valueOf(),
  urlEnum.preferences.valueOf(),
  urlEnum.welcome.valueOf(),
  urlEnum.validateTos.valueOf(),
  urlEnum.subscriptionPro.valueOf(),
  urlEnum.contactUs.valueOf(),
  urlEnum.resetPassword.valueOf(),
];
